import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { AlertTriangle, Loader2, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AntiSingleModal = ({ isOpen, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [violations, setViolations] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [fixing, setFixing] = useState(false);

  // Scanner les violations au chargement
  useEffect(() => {
    if (isOpen) {
      scanViolations();
    }
  }, [isOpen]);

  const scanViolations = async () => {
    setScanning(true);
    try {
      const response = await fetch(`${API_URL}/api/tickets/fix-anti-single-violations?dry_run=true`, {
        method: 'POST'
      });
      const data = await response.json();
      
      if (data.success) {
        setViolations(data.violations || []);
        // Sélectionner toutes par défaut
        setSelectedIds(new Set(data.violations?.map((_, idx) => idx) || []));
      } else {
        toast.error('Erreur lors du scan: ' + (data.error || 'Erreur inconnue'));
      }
    } catch (error) {
      toast.error('Erreur: ' + error.message);
    } finally {
      setScanning(false);
    }
  };

  const toggleSelection = (idx) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(idx)) {
        newSet.delete(idx);
      } else {
        newSet.add(idx);
      }
      return newSet;
    });
  };

  const selectAll = () => {
    setSelectedIds(new Set(violations.map((_, idx) => idx)));
  };

  const deselectAll = () => {
    setSelectedIds(new Set());
  };

  const handleFix = async () => {
    if (selectedIds.size === 0) {
      toast.error('Aucune violation sélectionnée');
      return;
    }

    setFixing(true);
    try {
      // Récupérer les refs_vente des violations sélectionnées
      const selectedViolations = violations.filter((_, idx) => selectedIds.has(idx));
      const refsToFix = [];
      selectedViolations.forEach(v => {
        (v.refs_vente_a_desattribuer || []).forEach(ref => {
          if (ref && !refsToFix.includes(ref)) {
            refsToFix.push(ref);
          }
        });
      });

      if (refsToFix.length === 0) {
        toast.error('Aucune ref_vente à désattribuer');
        setFixing(false);
        return;
      }

      // Appeler l'API pour désattribuer les refs sélectionnées
      const response = await fetch(`${API_URL}/api/tickets/desattribuer-by-refs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refs_vente: refsToFix })
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success(`✅ ${data.message}`);
        onSuccess?.();
        onClose();
      } else {
        toast.error('Erreur: ' + (data.detail || data.error || 'Erreur inconnue'));
      }
    } catch (error) {
      toast.error('Erreur: ' + error.message);
    } finally {
      setFixing(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col bg-zinc-900 border-zinc-700">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#C4A052]">
            <AlertTriangle className="h-5 w-5 text-orange-500" />
            Correction Règle Anti-Single
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex flex-col">
          {scanning ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-[#C4A052]" />
              <span className="ml-3 text-zinc-400">Scan des violations en cours...</span>
            </div>
          ) : violations.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <CheckCircle className="h-16 w-16 text-emerald-500 mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">Aucune violation trouvée !</h3>
              <p className="text-zinc-400">Tous les comptes respectent la règle anti-single.</p>
            </div>
          ) : (
            <>
              {/* Header avec compteur et boutons */}
              <div className="flex items-center justify-between mb-4 px-1">
                <div className="flex items-center gap-4">
                  <span className="text-orange-500 font-bold text-lg">
                    {violations.length} violation(s) trouvée(s)
                  </span>
                  <span className="text-zinc-400 text-sm">
                    {selectedIds.size} sélectionnée(s)
                  </span>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={selectAll}
                    className="text-xs border-zinc-600 hover:bg-zinc-800"
                  >
                    Tout sélectionner
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={deselectAll}
                    className="text-xs border-zinc-600 hover:bg-zinc-800"
                  >
                    Tout désélectionner
                  </Button>
                </div>
              </div>

              {/* Liste des violations */}
              <div className="flex-1 overflow-y-auto border border-zinc-700 rounded-lg">
                {violations.map((violation, idx) => (
                  <div
                    key={idx}
                    className={`flex items-center gap-3 p-3 border-b border-zinc-800 hover:bg-zinc-800/50 cursor-pointer transition-colors ${
                      selectedIds.has(idx) ? 'bg-zinc-800/30' : ''
                    }`}
                    onClick={() => toggleSelection(idx)}
                  >
                    <Checkbox
                      checked={selectedIds.has(idx)}
                      onCheckedChange={() => toggleSelection(idx)}
                      className="border-zinc-600 data-[state=checked]:bg-orange-500 data-[state=checked]:border-orange-500"
                    />
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="bg-orange-500/20 text-orange-400 text-xs font-bold px-2 py-0.5 rounded">
                          {violation.match}
                        </span>
                        <span className="bg-zinc-700 text-zinc-300 text-xs px-2 py-0.5 rounded">
                          {violation.categorie}
                        </span>
                        <span className="text-red-400 text-xs font-semibold">
                          {violation.restants_orphelins}/{violation.total_billets} orphelins
                        </span>
                      </div>
                      
                      <div className="flex items-center gap-2 text-sm">
                        <span className="text-zinc-300 truncate" title={violation.email}>
                          {violation.email}
                        </span>
                        {violation.source && (
                          <span className="text-zinc-500 text-xs">
                            • {violation.source}
                          </span>
                        )}
                      </div>
                      
                      {violation.refs_vente_a_desattribuer?.length > 0 && (
                        <div className="text-xs text-zinc-500 mt-1">
                          Refs: {violation.refs_vente_a_desattribuer.join(', ')}
                        </div>
                      )}
                    </div>
                    
                    <div className="text-right">
                      <div className="text-xs text-zinc-500">Attribués</div>
                      <div className="text-lg font-bold text-blue-400">{violation.attribues}</div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Footer avec boutons d'action */}
        <div className="flex justify-end gap-3 mt-4 pt-4 border-t border-zinc-800">
          <Button
            variant="outline"
            onClick={onClose}
            className="border-zinc-600 hover:bg-zinc-800"
          >
            Annuler
          </Button>
          
          {violations.length > 0 && (
            <Button
              onClick={handleFix}
              disabled={fixing || selectedIds.size === 0}
              className="bg-orange-500 hover:bg-orange-600 text-white"
            >
              {fixing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Correction en cours...
                </>
              ) : (
                <>
                  <XCircle className="h-4 w-4 mr-2" />
                  Désattribuer ({selectedIds.size})
                </>
              )}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AntiSingleModal;
